import { useQuery } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EEventFormFieldType } from '__generated__/globalTypes';
import 'ag-grid-community/styles/ag-grid.css'; // Mandatory CSS required by the grid
import 'ag-grid-community/styles/ag-theme-quartz.css'; // Optional Theme applied to the grid
import { AdminHeader } from 'components/admin/header/AdminHeader';
import { GuideInputBoolean } from 'components/admin/input/GuideInputBoolean';
import { GuideInputText } from 'components/admin/input/GuideInputText';
import { GuideTitle } from 'components/admin/visual/GuideTitle';
import { EventFormFields } from 'components/eventView/eventForm/EventFormFields';
import { PCScreen } from 'components/guide/PCScreen';
import { IconButton } from 'elements/eventView/atomic/IconButton';
import { SelectValue } from 'elements/minute/sidebar/sidebarComponents/atomic/SelectValue';
import { TextValue } from 'elements/minute/sidebar/sidebarComponents/atomic/TextValue';
import {
    CurrentUserForm,
    CurrentUserForm_currentUserForm,
    CurrentUserForm_currentUserForm_fields,
    CurrentUserForm_currentUserForm_fields_options,
    CurrentUserFormVariables
} from 'graphql/query/__generated__/CurrentUserForm';
import { FORM_CURRENT_USER_QUERY } from 'graphql/query/guideQuery';
import { useFormMutations } from 'hooks/forms/useFormMutations';
import { omitDeep } from 'libs/utils/object/objectOperations';
import { S_PageStyle } from 'pages/page/PageStyle';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';
import { useUpdateEffect } from 'usehooks-ts';
import { v4 as uuidv4 } from 'uuid';

interface Props {
    className?: string;
}

export const FormEditPage = (props: Props): JSX.Element => {
    const { eventFormId } = useParams() as { eventFormId: string };
    const formMutations = useFormMutations();

    const formQuery = useQuery<CurrentUserForm, CurrentUserFormVariables>(FORM_CURRENT_USER_QUERY, {
        variables: { eventFormId }
    });

    const [form, setForm] = useState<CurrentUserForm_currentUserForm | null>(null);

    useEffect(() => {
        if (!form && formQuery.data?.currentUserForm) {
            setForm(formQuery.data.currentUserForm);
        }
    }, [formQuery.data?.currentUserForm]);

    useUpdateEffect(() => {
        if (form) {
            formMutations.update(
                form.eventFormId,
                omitDeep(
                    {
                        title: form.title,
                        thankYouMessage: form.thankYouMessage,
                        allowSubmitMultiple: form.allowSubmitMultiple,
                        fields: form.fields
                    },
                    ['__typename']
                )
            );
        }
    }, [form]);

    if (!form) {
        return null;
    }

    function updateField(fieldId: string, data: Partial<CurrentUserForm_currentUserForm_fields>) {
        setForm((curr) => {
            return {
                ...curr,
                fields: curr.fields.map((field) => {
                    if (field.fieldId === fieldId) {
                        return {
                            ...field,
                            ...data
                        };
                    }

                    return field;
                })
            };
        });
    }

    function addField(index: number) {
        setForm((curr) => {
            return {
                ...curr,
                fields: [
                    ...curr.fields.slice(0, index),
                    {
                        fieldId: uuidv4(),
                        type: EEventFormFieldType.STRING as const,
                        title: '',
                        text: '',
                        placeholder: '',
                        validation: {
                            required: false,
                            maxLength: null,
                            __typename: 'EventFormFieldValidation'
                        },
                        options: [],
                        __typename: 'EventFormField'
                    },
                    ...curr.fields.slice(index)
                ]
            };
        });
    }

    function deleteField(fieldId: string) {
        setForm((curr) => {
            return {
                ...curr,
                fields: curr.fields.filter((field) => field.fieldId !== fieldId)
            };
        });
    }

    function moveField(fieldId: string, direction: 'up' | 'down') {
        const index = form.fields.findIndex((field) => field.fieldId === fieldId);
        if (direction === 'up') {
            setForm((curr) => {
                return {
                    ...curr,
                    fields: [
                        ...curr.fields.slice(0, index - 1),
                        curr.fields[index],
                        curr.fields[index - 1],
                        ...curr.fields.slice(index + 1)
                    ]
                };
            });
        }
        if (direction === 'down') {
            setForm((curr) => {
                return {
                    ...curr,
                    fields: [
                        ...curr.fields.slice(0, index),
                        curr.fields[index + 1],
                        curr.fields[index],
                        ...curr.fields.slice(index + 2)
                    ]
                };
            });
        }
    }

    function addFieldOption(fieldId: string) {
        setForm((curr) => {
            return {
                ...curr,
                fields: curr.fields.map((field) => {
                    if (field.fieldId === fieldId) {
                        return {
                            ...field,
                            options: [
                                ...field.options,
                                {
                                    optionId: uuidv4(),
                                    title: '',
                                    __typename: 'EventFormFieldOption'
                                }
                            ]
                        };
                    }

                    return field;
                })
            };
        });
    }

    function removeFieldOption(fieldId: string, optionId: string) {
        setForm((curr) => {
            return {
                ...curr,
                fields: curr.fields.map((field) => {
                    if (field.fieldId === fieldId) {
                        return {
                            ...field,
                            options: field.options.filter((option) => option.optionId !== optionId)
                        };
                    }

                    return field;
                })
            };
        });
    }

    function updateFieldOption(
        fieldId: string,
        optionId: string,
        option: Partial<CurrentUserForm_currentUserForm_fields_options>
    ) {
        setForm((curr) => {
            return {
                ...curr,
                fields: curr.fields.map((field) => {
                    if (field.fieldId === fieldId) {
                        return {
                            ...field,
                            options: field.options.map((opt) => {
                                if (opt.optionId === optionId) {
                                    return {
                                        ...opt,
                                        ...option
                                    };
                                }

                                return opt;
                            })
                        };
                    }

                    return field;
                })
            };
        });
    }

    const typeOptions = [
        {
            value: EEventFormFieldType.HEADLINE,
            label: 'Info - Headline'
        },
        {
            value: EEventFormFieldType.INFO_TEXT,
            label: 'Info - Text'
        },
        {
            value: EEventFormFieldType.STRING,
            label: 'Input - Short text'
        },
        {
            value: EEventFormFieldType.STRING_MULTILINE,
            label: 'Input - Long text'
        },
        {
            value: EEventFormFieldType.EMAIL,
            label: 'Input - Email'
        },
        {
            value: EEventFormFieldType.BOOLEAN,
            label: 'Input - Checkbox'
        },
        {
            value: EEventFormFieldType.RADIO,
            label: 'Input - Single option'
        },
        {
            value: EEventFormFieldType.MULTI_OPTIONS,
            label: 'Input - Multiple options'
        }
    ];

    return (
        <S_FormEditPage className={props.className}>
            <S_PCScreen>
                <AdminHeader />

                <S_Content>
                    <S_EditForm>
                        <S_GuideTitle title={Localization.FORM} />
                        <S_FormDetails>
                            <S_GuideInputText
                                label={Localization.FORM_TITLE}
                                value={form.title}
                                onChange={(value) => {
                                    setForm((curr) => ({
                                        ...curr,
                                        title: value
                                    }));
                                }}
                            />
                            <S_GuideInputText
                                label={Localization.FORM_THANK_YOU_MESSAGE}
                                value={form.thankYouMessage}
                                onChange={(value) => {
                                    setForm((curr) => ({
                                        ...curr,
                                        thankYouMessage: value
                                    }));
                                }}
                            />
                            <GuideInputBoolean
                                label={Localization.FORM_ALLOW_MULTIPLE_SUBMISSIONS}
                                value={form.allowSubmitMultiple}
                                onChange={(value) => {
                                    setForm((curr) => ({
                                        ...curr,
                                        allowSubmitMultiple: value
                                    }));
                                }}
                            />
                        </S_FormDetails>
                        <S_GuideTitle title={Localization.FORM_FIELDS} />

                        <S_Fields>
                            {form.fields.map((field, index) => {
                                return (
                                    <S_FieldContainer key={field.fieldId}>
                                        <S_IconAdd
                                            icon={['far', 'plus']}
                                            onClick={() => {
                                                addField(index);
                                            }}
                                        />
                                        <S_Field>
                                            <SelectValue
                                                options={typeOptions}
                                                selectedOption={typeOptions.find(
                                                    (option) => option.value === field.type
                                                )}
                                                onChange={(newValue) => {
                                                    updateField(field.fieldId, {
                                                        type: newValue.value as EEventFormFieldType
                                                    });
                                                }}
                                            />
                                            <S_GuideInputText
                                                label={Localization.FORM_TITLE}
                                                value={field.title}
                                                onChange={(newValue) => {
                                                    updateField(field.fieldId, { title: newValue });
                                                }}
                                            />
                                            <S_Validation>
                                                <GuideInputBoolean
                                                    label={Localization.REQUIRED}
                                                    value={field.validation?.required}
                                                    onChange={(newValue) => {
                                                        updateField(field.fieldId, {
                                                            validation: {
                                                                ...field.validation,
                                                                required: newValue
                                                            }
                                                        });
                                                    }}
                                                />
                                            </S_Validation>
                                            {field.type === EEventFormFieldType.INFO_TEXT && (
                                                <S_GuideInputText
                                                    label={Localization.FORM_FIELD_TEXT}
                                                    value={field.text}
                                                    onChange={(newValue) => {
                                                        updateField(field.fieldId, {
                                                            text: newValue
                                                        });
                                                    }}
                                                    multiple={true}
                                                    minRows={3}
                                                    maxRows={3}
                                                />
                                            )}
                                            {(field.type === EEventFormFieldType.RADIO ||
                                                field.type ===
                                                    EEventFormFieldType.MULTI_OPTIONS) && (
                                                <S_Options>
                                                    <S_FieldTitle>
                                                        {Localization.FORM_OPTIONS}
                                                    </S_FieldTitle>
                                                    {field.options.map((option) => {
                                                        return (
                                                            <S_Option key={option.optionId}>
                                                                <S_OptionInput
                                                                    value={option.title}
                                                                    placeholder="Option label"
                                                                    onChange={(newValue) => {
                                                                        updateFieldOption(
                                                                            field.fieldId,
                                                                            option.optionId,
                                                                            { title: newValue }
                                                                        );
                                                                    }}
                                                                />
                                                                <S_ControlButton
                                                                    iconName="trash"
                                                                    onClick={() => {
                                                                        removeFieldOption(
                                                                            field.fieldId,
                                                                            option.optionId
                                                                        );
                                                                    }}
                                                                />
                                                            </S_Option>
                                                        );
                                                    })}
                                                    <S_IconAddOption
                                                        iconName={'plus'}
                                                        onClick={() => {
                                                            addFieldOption(field.fieldId);
                                                        }}
                                                    />
                                                </S_Options>
                                            )}
                                            <S_ControlButtons>
                                                {index > 0 && (
                                                    <S_ControlButton
                                                        iconName="arrow-up"
                                                        onClick={() =>
                                                            moveField(field.fieldId, 'up')
                                                        }
                                                    />
                                                )}
                                                {index < form.fields.length - 1 && (
                                                    <S_ControlButton
                                                        iconName="arrow-down"
                                                        onClick={() =>
                                                            moveField(field.fieldId, 'down')
                                                        }
                                                    />
                                                )}
                                                <S_ControlButton
                                                    iconName="trash"
                                                    onClick={() => deleteField(field.fieldId)}
                                                />
                                            </S_ControlButtons>
                                        </S_Field>
                                    </S_FieldContainer>
                                );
                            })}
                            <S_IconAdd
                                icon={['far', 'plus']}
                                onClick={() => {
                                    addField(form.fields.length);
                                }}
                            />
                        </S_Fields>
                    </S_EditForm>
                    <S_Preview>
                        <S_GuideTitle title={form.title} />
                        <EventFormFields guideId="" formFields={form.fields} isLoading={false} />
                    </S_Preview>
                </S_Content>
            </S_PCScreen>
        </S_FormEditPage>
    );
};

export default FormEditPage;

const S_FormEditPage = styled(S_PageStyle)``;

const S_PCScreen = styled(PCScreen)`
    margin: 0 auto;
`;

const S_Content = styled.div`
    display: flex;
    flex-direction: row;
    margin: 0 auto 0 auto;
    width: 1260px;
    align-items: flex-start;
    justify-content: space-between;
    gap: 60px;
    margin-top: 60px;
    padding: 60px 0;
`;

const S_FormDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px;
    background: #f6f7f5;
    border-radius: 20px;
`;

const S_EditForm = styled.div`
    width: 600px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const S_Preview = styled.div`
    width: 440px;
`;

const S_GuideInputText = styled(GuideInputText)`
    width: 100%;
`;

const S_GuideTitle = styled(GuideTitle)`
    margin: 0px 0 0px 0;
`;

const S_Fields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const S_FieldContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px;
    background: #f6f7f5;
    border-radius: 20px;
`;

const S_IconAdd = styled(FontAwesomeIcon)`
    color: #041321;
    background: #f6f7f5;
    width: 20px;
    height: 20px;
    padding: 10px;
    border-radius: 50%;
    font-size: 20px;
    cursor: pointer;
    margin: 0px 0 20px 0;
    align-self: center;
`;

const S_ControlButtons = styled.div`
    align-self: flex-end;
    display: flex;
    flex-direction: row;
    gap: 5px;
`;

const S_ControlButton = styled(IconButton)``;

const S_FieldTitle = styled.div`
    font-size: 16px;
    color: #041321;
`;

const S_Options = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const S_Option = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    justify-content: flex-start;
    align-items: center;
`;

const S_OptionInput = styled(TextValue)`
    text-align: left;
    width: 300px;
`;

const S_IconAddOption = styled(IconButton)`
    align-self: flex-start;
`;

const S_Validation = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;
